import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  HowToReg as DraftIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import unifiedApi from '../utils/unifiedApi';
import appConfig from '../config/appConfig';
import { usePermissions } from '../contexts/PermissionsContext';

export const REGISTRATION_TYPE_LABELS = {
  OutfieldPlayer: 'Outfielder',
  Goalkeeper: 'Keeper',
  SpareOutfielder: 'Spare Outfielder',
  SpareGoalkeeper: 'Spare Keeper',
};

const REGISTRATION_TYPE_ORDER = ['OutfieldPlayer', 'Goalkeeper', 'SpareOutfielder', 'SpareGoalkeeper'];
const NOT_SPECIFIED = 'Not specified';

const SORT_OPTIONS = [
  { value: 'lastName', label: 'Last name' },
  { value: 'firstName', label: 'First name' },
  { value: 'age', label: 'Age' },
  { value: 'preferredPosition1', label: 'Preferred position' },
  { value: 'preferredPosition2', label: 'Second position' },
  { value: 'positionType', label: 'Registration type' },
  { value: 'expectedGamesToMiss', label: 'Games to miss' },
  { value: 'isNewToLeague', label: 'New to league' },
  { value: 'referral', label: 'Referred by' },
  { value: 'totalGamesPlayed', label: 'Games played' },
  { value: 'totalGamesMissed', label: 'Games missed' },
  { value: 'totalGoals', label: 'Goals scored' },
  { value: 'totalCards', label: 'Total cards' },
  { value: 'totalCleanSheets', label: 'Clean sheets' },
];

// Career-stat sorts default to descending so the strongest players surface first.
const DESC_DEFAULT_SORTS = new Set([
  'totalGamesPlayed',
  'totalGamesMissed',
  'totalGoals',
  'totalCards',
  'totalCleanSheets',
]);

const FILTER_GROUPS = [
  { key: 'positionType', label: 'Registration type' },
  { key: 'preferredPosition1', label: 'Preferred position' },
  { key: 'preferredPosition2', label: 'Second position' },
];

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const apiBase = appConfig.apiBaseUrl || 'http://localhost:5131';
  return `${apiBase}${url}`;
};

const getInitials = (player) => `${player.firstName?.[0] || ''}${player.lastName?.[0] || ''}` || '?';

const getFullName = (player) => `${player.firstName || ''} ${player.lastName || ''}`.trim();

const getFilterValue = (player, key) => player[key] || NOT_SPECIFIED;

const getFilterLabel = (key, value) => (
  key === 'positionType' ? (REGISTRATION_TYPE_LABELS[value] || value) : value
);

const getSortValue = (player, key) => {
  switch (key) {
    case 'positionType':
      return REGISTRATION_TYPE_ORDER.indexOf(player.positionType);
    case 'isNewToLeague':
      return player.isNewToLeague ? 1 : 0;
    case 'totalCards':
      return (player.totalYellowCards || 0) + (player.totalRedCards || 0);
    default:
      return player[key];
  }
};

const compareValues = (a, b) => {
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
};

const sortPlayers = (players, sortBy, sortDirection) => players.slice().sort((first, second) => {
  const firstValue = getSortValue(first, sortBy);
  const secondValue = getSortValue(second, sortBy);
  const firstMissing = firstValue == null || firstValue === '';
  const secondMissing = secondValue == null || secondValue === '';

  // Players without a value always sort last, regardless of direction.
  if (firstMissing !== secondMissing) return firstMissing ? 1 : -1;

  let comparison = firstMissing ? 0 : compareValues(firstValue, secondValue);
  if (sortDirection === 'desc') comparison = -comparison;
  if (comparison !== 0) return comparison;

  return compareValues(getFullName(first), getFullName(second));
});

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" sx={{ fontWeight: 500, textAlign: 'right', wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

const PlayerCard = ({ player, onPhotoClick }) => {
  const fullName = getFullName(player);
  const photoUrl = getImageUrl(player.profilePhotoUrl);
  const avatar = (
    <Avatar src={photoUrl || undefined} alt={fullName} sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: '1.5rem' }}>
      {getInitials(player)}
    </Avatar>
  );

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          {photoUrl ? (
            <ButtonBase
              onClick={() => onPhotoClick(player)}
              aria-label={`View larger photo of ${fullName}`}
              sx={{ borderRadius: '50%' }}
            >
              {avatar}
            </ButtonBase>
          ) : avatar}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3, wordBreak: 'break-word' }}>
              {player.userTenantId ? (
                <Box
                  component={Link}
                  to={`/player/${player.userTenantId}`}
                  sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  {fullName}
                </Box>
              ) : fullName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {player.age != null ? `Age ${player.age}` : 'Age unknown'}
            </Typography>
            <Chip
              label={REGISTRATION_TYPE_LABELS[player.positionType] || player.positionType}
              size="small"
              color={player.positionType?.startsWith('Spare') ? 'default' : 'primary'}
              sx={{ mt: 0.5 }}
              clickable={false}
              onClick={() => {}}
            />
          </Box>
        </Stack>
        <Stack spacing={0.5}>
          <DetailRow label="Preferred" value={player.preferredPosition1 || '—'} />
          <DetailRow label="Second" value={player.preferredPosition2 || '—'} />
          <DetailRow label="Games to miss" value={player.expectedGamesToMiss ?? 0} />
          <DetailRow label="New to league" value={player.isNewToLeague ? 'Yes' : 'No'} />
          {player.referral && <DetailRow label="Referred by" value={player.referral} />}
        </Stack>
        <Divider sx={{ my: 1.5 }} />
        <Stack spacing={0.5}>
          <DetailRow label="Games played" value={player.totalGamesPlayed ?? 0} />
          <DetailRow label="Games missed" value={player.totalGamesMissed ?? 0} />
          <DetailRow label="Missed (injury)" value={player.totalGamesMissedInjury ?? 0} />
          <DetailRow label="Goals" value={player.totalGoals ?? 0} />
          <DetailRow label="Seasons played" value={player.totalSeasonsPlayed ?? 0} />
          {player.goalsPerGame != null && (
            <DetailRow label="Goals/game" value={player.goalsPerGame.toFixed(2)} />
          )}
          {player.totalYellowCards > 0 && (
            <DetailRow label="Yellow cards" value={player.totalYellowCards} />
          )}
          {player.totalRedCards > 0 && (
            <DetailRow label="Red cards" value={player.totalRedCards} />
          )}
          {player.totalCleanSheets > 0 && (
            <DetailRow label="Clean sheets" value={player.totalCleanSheets} />
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const LeagueDraft = () => {
  const { leagueSlug } = useParams();
  const { permissions, isLoading: permissionsLoading, error: permissionsError, canViewLeagueDraft } = usePermissions();
  const permissionsPending = permissionsLoading || (!permissions && !permissionsError);

  const [league, setLeague] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('lastName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [excluded, setExcluded] = useState({ positionType: [], preferredPosition1: [], preferredPosition2: [] });
  const [photoPlayer, setPhotoPlayer] = useState(null);

  const canView = league ? canViewLeagueDraft(league.id) : false;

  useEffect(() => {
    let cancelled = false;
    const loadLeague = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await unifiedApi.getLeagueBySlug(leagueSlug);
        if (cancelled) return;
        if (!data) {
          setError('League not found.');
          setLoading(false);
          return;
        }
        setLeague(data);
      } catch (err) {
        if (!cancelled) {
          setError('Failed to load league.');
          setLoading(false);
        }
      }
    };
    loadLeague();
    return () => { cancelled = true; };
  }, [leagueSlug]);

  useEffect(() => {
    if (!league || permissionsPending) return undefined;
    if (!canView) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loadDraft = async () => {
      setLoading(true);
      try {
        const data = await unifiedApi.getLeagueDraft(league.id);
        if (!cancelled) setDraft(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load draft players.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadDraft();
    return () => { cancelled = true; };
  }, [league, canView, permissionsPending]);

  const players = useMemo(() => draft?.players || [], [draft]);

  const filterOptions = useMemo(() => FILTER_GROUPS.reduce((options, group) => {
    const values = Array.from(new Set(players.map(p => getFilterValue(p, group.key))));
    values.sort((a, b) => {
      if (group.key === 'positionType') {
        return REGISTRATION_TYPE_ORDER.indexOf(a) - REGISTRATION_TYPE_ORDER.indexOf(b);
      }
      if (a === NOT_SPECIFIED) return 1;
      if (b === NOT_SPECIFIED) return -1;
      return a.localeCompare(b);
    });
    return { ...options, [group.key]: values };
  }, {}), [players]);

  const visiblePlayers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const filtered = players.filter(player => (
      FILTER_GROUPS.every(group => !excluded[group.key].includes(getFilterValue(player, group.key)))
      && (!term || getFullName(player).toLowerCase().includes(term))
    ));
    return sortPlayers(filtered, sortBy, sortDirection);
  }, [players, excluded, searchTerm, sortBy, sortDirection]);

  const hasActiveFilters = searchTerm.trim() !== '' || FILTER_GROUPS.some(group => excluded[group.key].length > 0);

  const toggleFilter = (groupKey, value) => {
    setExcluded(current => {
      const values = current[groupKey];
      return {
        ...current,
        [groupKey]: values.includes(value) ? values.filter(v => v !== value) : [...values, value],
      };
    });
  };

  const resetFilters = () => {
    setExcluded({ positionType: [], preferredPosition1: [], preferredPosition2: [] });
    setSearchTerm('');
  };

  const handleSortChange = (value) => {
    setSortBy(value);
    if (DESC_DEFAULT_SORTS.has(value)) {
      setSortDirection('desc');
    }
  };

  if (loading || (league && permissionsPending)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!canView) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', py: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>Access denied</Typography>
          <Typography color="text.secondary">You do not have permission to view this league's draft.</Typography>
        </Paper>
      </Box>
    );
  }

  const photoPlayerName = photoPlayer ? getFullName(photoPlayer) : '';

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography
          variant="h4"
          sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '1.5rem', sm: '2.125rem' }, wordBreak: 'break-word' }}
        >
          <DraftIcon color="primary" />
          {draft?.leagueName || league?.name} Draft
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Showing {visiblePlayers.length} of {players.length} players
        </Typography>
      </Stack>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: { xs: '100%', md: 280 } }}
            inputProps={{ 'aria-label': 'Search players by name' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: '100%', md: 'auto' } }}>
            <FormControl size="small" sx={{ flexGrow: 1, minWidth: { md: 200 } }}>
              <InputLabel id="draft-sort-label">Sort by</InputLabel>
              <Select
                labelId="draft-sort-label"
                value={sortBy}
                label="Sort by"
                onChange={(e) => handleSortChange(e.target.value)}
              >
                {SORT_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}>
              <IconButton
                aria-label={sortDirection === 'asc' ? 'Sort descending' : 'Sort ascending'}
                onClick={() => setSortDirection(current => (current === 'asc' ? 'desc' : 'asc'))}
              >
                {sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
              </IconButton>
            </Tooltip>
          </Stack>
          {hasActiveFilters && (
            <Button onClick={resetFilters} sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}>
              Reset filters
            </Button>
          )}
        </Stack>

        <Stack spacing={1.5}>
          {FILTER_GROUPS.map(group => (filterOptions[group.key]?.length > 0 && (
            <Box key={group.key}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {group.label}
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" role="group" aria-label={`${group.label} filters`}>
                {filterOptions[group.key].map(value => {
                  const selected = !excluded[group.key].includes(value);
                  return (
                    <Chip
                      key={value}
                      label={getFilterLabel(group.key, value)}
                      size="small"
                      icon={selected ? <CheckIcon /> : undefined}
                      color={selected ? 'primary' : 'default'}
                      variant={selected ? 'filled' : 'outlined'}
                      onClick={() => toggleFilter(group.key, value)}
                      aria-pressed={selected}
                    />
                  );
                })}
              </Stack>
            </Box>
          )))}
        </Stack>
      </Paper>

      {players.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No paid registrations for this league yet.</Typography>
        </Paper>
      ) : visiblePlayers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No players match the current filters.</Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
            gap: 2,
          }}
        >
          {visiblePlayers.map(player => (
            <PlayerCard key={player.registrationId} player={player} onPhotoClick={setPhotoPlayer} />
          ))}
        </Box>
      )}

      <Dialog open={Boolean(photoPlayer)} onClose={() => setPhotoPlayer(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 6, wordBreak: 'break-word' }}>
          {photoPlayerName}
          <IconButton
            aria-label="Close photo"
            onClick={() => setPhotoPlayer(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center' }}>
          {photoPlayer && (
            <Box
              component="img"
              src={getImageUrl(photoPlayer.profilePhotoUrl)}
              alt={photoPlayerName}
              sx={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 1, objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LeagueDraft;
